/**
 * Sprint 62 (auth-frontend-impl-s62) — first-visit **start-screen modal**.
 *
 * Drawn on top of the title screen before the player picks Sign in or
 * Play as guest. Once dismissed (by a click on any of the choices), it
 * stays dismissed until an explicit Sign out — the choice is persisted
 * on the profile blob (`Profile.guestMode`, v4 schema) so the modal
 * re-shows only when the player explicitly resets that choice.
 *
 * The frontend ships **first**; the backend (Cognito + Discord/Google +
 * the session cookie) lives in a separate sprint and is documented in
 * `docs/AUTH_DESIGN.md` §§2 / 7. Until that lands, the three sign-in
 * buttons all resolve to the friendly "Coming soon" placeholder via
 * {@link signInWithDiscord} / {@link signInWithGoogle} / {@link signInWithEmail},
 * which post a transient HUD notice instead of attempting the OAuth
 * popup. The stubs are kept tiny so the future backend swap is a
 * one-call change at each site — no plumbing rework.
 *
 * UI-only — never touches `GameState`, never reads / writes the
 * deterministic simulation. Render + input mirrors the `puzzleSelect.ts`
 * / `onboarding.ts` overlay conventions: a centred dialog with neon
 * trim, modal input that absorbs every other gesture while it is open.
 *
 * The modal is a sibling of the cookies banner (`./cookies.ts`); both
 * may show at the same time on a first visit. The banner sits at the
 * bottom of the canvas, the modal centred — they do not overlap.
 */

import { CONFIG } from '../config';
import { roundRect, withAlpha } from '../engine/util';
import { t } from '../i18n';
import { saveProfile, type Profile } from '../game/profile';

const C = CONFIG.colors;
const FONT_DISPLAY = '"Chakra Petch", "Rajdhani", system-ui, sans-serif';
const FONT_MONO = '"Share Tech Mono", ui-monospace, "DejaVu Sans Mono", monospace';
const CW = CONFIG.canvas.width;
const CH = CONFIG.canvas.height;

/** A click region inside the modal — canvas-space pixels. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The modal panel geometry. Centred on the canvas; mirrors the intro card. */
const PANEL: Rect = { x: (CW - 460) / 2, y: 92, w: 460, h: 412 };

/** A stacked column of three sign-in buttons + one Play-as-guest button. */
const BUTTON_W = 360;
const BUTTON_H = 44;
const BUTTON_GAP = 12;
const BUTTON_X = PANEL.x + (PANEL.w - BUTTON_W) / 2;
const BUTTON_Y0 = PANEL.y + 168;

const BTN_DISCORD: Rect = { x: BUTTON_X, y: BUTTON_Y0, w: BUTTON_W, h: BUTTON_H };
const BTN_GOOGLE: Rect = {
  x: BUTTON_X,
  y: BUTTON_Y0 + (BUTTON_H + BUTTON_GAP),
  w: BUTTON_W,
  h: BUTTON_H,
};
const BTN_EMAIL: Rect = {
  x: BUTTON_X,
  y: BUTTON_Y0 + 2 * (BUTTON_H + BUTTON_GAP),
  w: BUTTON_W,
  h: BUTTON_H,
};
/** The guest button sits below a thin "or" divider — visually demoted. */
const BTN_GUEST: Rect = {
  x: BUTTON_X,
  y: BUTTON_Y0 + 3 * (BUTTON_H + BUTTON_GAP) + 18,
  w: BUTTON_W,
  h: BUTTON_H,
};

/**
 * The set of click targets the modal recognises. The orchestrator (`Game`)
 * routes each kind through the appropriate stub or profile mutation.
 *
 * - `discord` / `google` / `email` — fire one of the stubbed sign-in
 *   helpers below. The user gets a "Coming soon" HUD notice; the modal
 *   stays open so the player can still pick Play-as-guest.
 * - `guest` — persist `guestMode=true` and dismiss the modal.
 */
export type StartHit =
  | { kind: 'discord' }
  | { kind: 'google' }
  | { kind: 'email' }
  | { kind: 'guest' };

/**
 * Should the start-screen modal be visible right now? `true` while the
 * player has not yet made a choice (`guestMode === null`). The orchestrator
 * gates input and render on this predicate, so a profile-reset call (or
 * the future Sign-out flow) re-shows the modal automatically.
 */
export function isStartScreenOpen(profile: Profile): boolean {
  return profile.guestMode === null;
}

/**
 * Resolve a click at (`x`, `y`) to a {@link StartHit}, or `null` when the
 * click misses every button. Clicks outside the dialog still return null —
 * the modal is dismiss-by-choice, not dismiss-by-outside-click (mirrors
 * `puzzleSelect.ts`).
 */
export function startScreenHit(x: number, y: number): StartHit | null {
  if (inRect(BTN_DISCORD, x, y)) return { kind: 'discord' };
  if (inRect(BTN_GOOGLE, x, y)) return { kind: 'google' };
  if (inRect(BTN_EMAIL, x, y)) return { kind: 'email' };
  if (inRect(BTN_GUEST, x, y)) return { kind: 'guest' };
  return null;
}

/**
 * Discord OAuth stub — today this returns the "Coming soon" placeholder
 * string the caller posts as a HUD notice. When the backend ships in
 * `auth-backend-s63`, swap the body for the popup-OAuth recipe in
 * `docs/AUTH_DESIGN.md` §3.2 (popup → Cognito Hosted UI → postMessage
 * → POST /auth/exchange). The signature stays stable so the caller's
 * one line doesn't change.
 */
export function signInWithDiscord(): { placeholder: string } {
  return { placeholder: t('auth.comingSoon') };
}

/**
 * Google OAuth stub — see {@link signInWithDiscord}. Same shape today
 * (the placeholder string); same shape after the backend ships.
 */
export function signInWithGoogle(): { placeholder: string } {
  return { placeholder: t('auth.comingSoon') };
}

/**
 * Email magic-link stub — see {@link signInWithDiscord}. Same shape.
 * The email path is gated on the same backend ship as the OAuth paths;
 * today it posts the same friendly placeholder.
 */
export function signInWithEmail(): { placeholder: string } {
  return { placeholder: t('auth.comingSoon') };
}

/**
 * Persist the player's "Play as guest" pick on the profile. Idempotent;
 * a re-call is a no-op (the modal is only re-shown after an explicit
 * sign-out). The caller drops the modal by `isStartScreenOpen` flipping
 * to `false` on the next frame.
 *
 * `name` mirrors the `?profile=<name>` plumbing in `Game.ts` — pass it
 * through so the choice writes to the correct profile slot (default
 * un-named when omitted).
 */
export function acceptGuestMode(profile: Profile, name?: string): void {
  if (profile.guestMode === true) return;
  profile.guestMode = true;
  saveProfile(profile, name);
}

/**
 * Sign the player out — reset {@link Profile.guestMode} to `null` so the
 * start-screen modal re-opens on the next render. In this sprint there
 * is no actual session cookie to clear (the backend is stubbed); the
 * future backend swap drops the `overvolt_sid` cookie at the same call
 * site and posts to `/auth/exchange?signout` per `docs/AUTH_DESIGN.md`
 * §3.3.
 *
 * Idempotent — a re-call on a fresh-modal profile is a no-op.
 */
export function signOut(profile: Profile, name?: string): void {
  if (profile.guestMode === null) return;
  profile.guestMode = null;
  saveProfile(profile, name);
}

/**
 * True when the player has signed in (`guestMode === false`). In this
 * sprint that is **never** reached at runtime — the only paths set
 * `guestMode` are the Play-as-guest button (sets `true`) and the future
 * sign-in success (sets `false`). The Profile-screen Sign-out button
 * consumes this predicate so the row hides for guests today and lights
 * up for free once the backend ships.
 */
export function isSignedIn(profile: Profile): boolean {
  return profile.guestMode === false;
}

/** Draw the modal — call once per frame from the renderer while open. */
export function drawStartScreen(ctx: CanvasRenderingContext2D): void {
  const time = performance.now() / 1000;

  // Backdrop — dim the title screen behind so the dialog reads as a real
  // modal (mirrors the intro overlay's 0.9 alpha). We leave the bottom
  // strip a touch lighter so the cookies banner is still legible.
  ctx.save();
  ctx.fillStyle = 'rgba(5, 8, 16, 0.88)';
  ctx.fillRect(0, 0, CW, CH);

  // Panel.
  neonPanel(ctx, PANEL.x, PANEL.y, PANEL.w, PANEL.h, C.accent, time);

  const cx = PANEL.x + PANEL.w / 2;

  // Title (OVERVOLT brand).
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  glowText(ctx, t('auth.title'), cx, PANEL.y + 64, `700 36px ${FONT_DISPLAY}`, C.accent, 18);

  // Subtitle — wrapped body copy.
  ctx.font = `12px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.hudText, 0.9);
  const subLines = wrapText(ctx, t('auth.subtitle'), PANEL.w - 80);
  let sy = PANEL.y + 94;
  for (const line of subLines) {
    ctx.fillText(line, cx, sy);
    sy += 17;
  }

  // Three sign-in buttons + one Play-as-guest button.
  drawSignInButton(ctx, BTN_DISCORD, t('auth.signInDiscord'), '#5865F2');
  drawSignInButton(ctx, BTN_GOOGLE, t('auth.signInGoogle'), '#ffffff');
  drawSignInButton(ctx, BTN_EMAIL, t('auth.signInEmail'), withAlpha(C.accent, 0.9));

  // Soft "or" divider above the guest button — visual demote without making
  // the guest path read as a fallback.
  ctx.textAlign = 'center';
  ctx.font = `10px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.hudMuted, 0.85);
  ctx.textBaseline = 'middle';
  const dy = BTN_GUEST.y - 12;
  ctx.fillText(t('auth.orDivider'), cx, dy);
  // A pair of short rules either side of "or".
  ctx.strokeStyle = withAlpha(C.hudMuted, 0.3);
  ctx.lineWidth = 1;
  const orW = ctx.measureText(t('auth.orDivider')).width;
  ctx.beginPath();
  ctx.moveTo(cx - orW / 2 - 12, dy);
  ctx.lineTo(BUTTON_X + 30, dy);
  ctx.moveTo(cx + orW / 2 + 12, dy);
  ctx.lineTo(BUTTON_X + BUTTON_W - 30, dy);
  ctx.stroke();

  drawGuestButton(ctx, BTN_GUEST, t('auth.playAsGuest'), time);
  ctx.restore();
}

/** Draw one sign-in button — coloured rail + label, neutral fill. */
function drawSignInButton(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  label: string,
  color: string,
): void {
  ctx.save();
  // Body.
  ctx.fillStyle = 'rgba(14, 22, 42, 0.92)';
  roundRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.fill();
  // Coloured rail on the left edge.
  ctx.fillStyle = color;
  roundRect(ctx, r.x, r.y, 4, r.h, 2);
  ctx.fill();
  // Outline.
  ctx.strokeStyle = withAlpha(color, 0.65);
  ctx.lineWidth = 1.4;
  roundRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.stroke();
  // Label.
  ctx.fillStyle = C.hudText;
  ctx.font = `600 14px ${FONT_DISPLAY}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, r.x + 22, r.y + r.h / 2 + 1);
  ctx.restore();
}

/** Guest button — same shape but in the OVERVOLT accent cyan with a pulse. */
function drawGuestButton(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  label: string,
  time: number,
): void {
  const pulse = 0.6 + 0.4 * Math.sin(time * 2.6);
  ctx.save();
  ctx.fillStyle = withAlpha(C.accent, 0.14 + 0.08 * pulse);
  roundRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.6 + 0.35 * pulse);
  ctx.lineWidth = 1.6;
  ctx.shadowBlur = 12 * pulse;
  ctx.shadowColor = C.accent;
  roundRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.fillStyle = C.accent;
  ctx.font = `700 14px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  ctx.restore();
}

// --- shared drawing helpers (mirror onboarding.ts / puzzleSelect.ts) -------

function inRect(b: Rect, x: number, y: number): boolean {
  return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
}

/** A neon dialog panel with a soft glow and corner brackets. */
function neonPanel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  time: number,
): void {
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, 'rgba(14, 22, 42, 0.98)');
  g.addColorStop(1, 'rgba(8, 12, 24, 0.98)');
  ctx.fillStyle = g;
  roundRect(ctx, x, y, w, h, 12);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.55 + 0.22 * Math.sin(time * 2.5));
  ctx.lineWidth = 1.5;
  ctx.shadowBlur = 24;
  ctx.shadowColor = color;
  roundRect(ctx, x, y, w, h, 12);
  ctx.stroke();
  ctx.shadowBlur = 0;
  // Corner brackets — the OVERVOLT panel signature.
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  const k = 18;
  const corner = (px: number, py: number, sx: number, sy: number): void => {
    ctx.beginPath();
    ctx.moveTo(px + sx * k, py);
    ctx.lineTo(px, py);
    ctx.lineTo(px, py + sy * k);
    ctx.stroke();
  };
  corner(x + 7, y + 7, 1, 1);
  corner(x + w - 7, y + 7, -1, 1);
  corner(x + 7, y + h - 7, 1, -1);
  corner(x + w - 7, y + h - 7, -1, -1);
}

/** Centre-aligned text with a glow. */
function glowText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  font: string,
  color: string,
  glow: number,
): void {
  ctx.save();
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.fillStyle = color;
  ctx.shadowBlur = glow;
  ctx.shadowColor = color;
  ctx.fillText(text, x, y);
  ctx.restore();
}

/** Greedily wrap `text` to lines no wider than `maxWidth` (ctx font preset). */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

